/**
 * Session store (Zustand) — the single place the mock "who am I / what am I
 * looking at" state lives, shaped like the real JWT session that will populate
 * it post-login (user + active business + role from memberships, Decision #9).
 *
 * Today it's seeded with the mock owner and is NOT enforced as a route guard
 * (routing is still URL-prefix based) — adding guards is a later step so it
 * can't strand a page during the mock phase. The dev role switcher lets the
 * account-chooser / BusinessSwitcher exercise the same state the real session
 * will drive.
 */
import { create } from 'zustand';

export type SessionUser = { id: string; name: string; email: string | null };

/** Which shell the user is currently in — maps 1:1 to the four route shells. */
export type ActiveRole = 'owner' | 'admin' | 'reseller' | 'staff';

/**
 * Tokens are kept IN MEMORY only — never in localStorage/sessionStorage, which
 * are JS-reachable and so XSS-exfiltratable (a stolen refresh token is long-lived
 * account compromise). Cost: a hard reload drops the session and the user signs
 * in again. The proper "survive reload" fix is an httpOnly+Secure+SameSite=Strict
 * cookie for the refresh token, set by /auth/login and sent via credentials:
 * 'include' — wire that when the auth integration is completed.
 */
type SessionState = {
  user: SessionUser | null;
  activeRole: ActiveRole;
  activeBusinessId: string | null;
  activeResellerId: string | null;
  /** Real-API auth (null in mock mode). The api client sends accessToken as a Bearer. */
  accessToken: string | null;
  refreshToken: string | null;
  /**
   * Read-only impersonation (admin "Open as"). When set, the app runs under a
   * short-lived impersonation token; `savedSession` is the admin session to put
   * back on Exit. Both null in a normal session.
   */
  impersonation: ImpersonationState | null;
  savedSession: SavedSession | null;
  setUser: (user: SessionUser | null) => void;
  setActiveRole: (role: ActiveRole) => void;
  setActiveBusiness: (businessId: string | null) => void;
  setActiveReseller: (resellerId: string | null) => void;
  setTokens: (access: string | null, refresh: string | null) => void;
  startImpersonation: (accessToken: string, target: { id: string; name: string; role: ActiveRole }, returnTo: string) => void;
  exitImpersonation: () => void;
  signOut: () => void;
};

export type ImpersonationState = { targetName: string; targetRole: ActiveRole; returnTo: string };
type SavedSession = {
  user: SessionUser | null;
  activeRole: ActiveRole;
  activeBusinessId: string | null;
  activeResellerId: string | null;
  accessToken: string | null;
};

export const useSession = create<SessionState>((set) => ({
  user: { id: 'usr_10', name: 'Imran Khan', email: 'imran@khan-bakery.com' },
  activeRole: 'owner',
  activeBusinessId: 'biz_42',
  activeResellerId: null,
  accessToken: null,
  refreshToken: null,
  impersonation: null,
  savedSession: null,
  setUser: (user) => set({ user }),
  setActiveRole: (activeRole) => set({ activeRole }),
  setActiveBusiness: (activeBusinessId) => set({ activeBusinessId }),
  setActiveReseller: (activeResellerId) => set({ activeResellerId }),
  setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
  startImpersonation: (accessToken, target, returnTo) =>
    set((s) => ({
      savedSession: {
        user: s.user, activeRole: s.activeRole, activeBusinessId: s.activeBusinessId,
        activeResellerId: s.activeResellerId, accessToken: s.accessToken,
      },
      impersonation: { targetName: target.name, targetRole: target.role, returnTo },
      user: { id: target.id, name: target.name, email: null },
      activeRole: target.role,
      activeResellerId: target.role === 'reseller' ? target.id : null,
      activeBusinessId: target.role === 'owner' || target.role === 'staff' ? target.id : null,
      accessToken,
    })),
  exitImpersonation: () =>
    set((s) => {
      const saved = s.savedSession;
      if (!saved) return { impersonation: null };
      return {
        impersonation: null,
        savedSession: null,
        user: saved.user,
        activeRole: saved.activeRole,
        activeBusinessId: saved.activeBusinessId,
        activeResellerId: saved.activeResellerId,
        accessToken: saved.accessToken,
      };
    }),
  signOut: () =>
    set({ user: null, activeBusinessId: null, activeResellerId: null, accessToken: null, refreshToken: null, impersonation: null, savedSession: null }),
}));
